import {
  FileInput,
  GitMerge,
  Mail,
  MessageSquare,
  Rocket,
  Search,
  Smartphone,
  SquarePen,
  StickyNote,
  Tag,
  Target,
  Trash2,
  TrendingUp,
} from "lucide-react"

import { ActionDefinition } from "@/components/automations/types"

export const householdActions: ActionDefinition[] = [
  {
    key: "action__add_tag",
    name: "Add tag",
    description: "Add a tag to the household",
    category: "household",
    nodeType: "action",
    recordType: "household",
    icon: Tag,
    requiresSubjects: ["household"],
    fields: [
      {
        key: "tag_id",
        label: "Tag",
        inputType: "select",
        required: true,
        optionsSource: "tags",
      },
    ],
  },
  {
    key: "action__remove_tag",
    name: "Remove tag",
    description: "Remove a tag from the household",
    category: "household",
    nodeType: "action",
    recordType: "household",
    icon: Tag,
    requiresSubjects: ["household"],
    fields: [
      {
        key: "tag_id",
        label: "Tag",
        inputType: "select",
        required: true,
        optionsSource: "tags",
      },
    ],
  },
  {
    key: "action__add_note",
    name: "Add note",
    description: "Add a note to the household record",
    category: "household",
    nodeType: "action",
    recordType: "household",
    icon: StickyNote,
    requiresSubjects: ["household"],
    fields: [
      {
        key: "content",
        label: "Note content",
        inputType: "markdown",
        required: true,
        supportsInterpolation: true,
      },
      {
        key: "tag_id",
        label: "Label",
        inputType: "select",
        required: false,
        optionsSource: "note_tags",
      },
    ],
  },
  {
    key: "action__create_opportunity",
    name: "Create opportunity",
    description: "Create an opportunity for the household",
    category: "household",
    nodeType: "action",
    recordType: "household",
    icon: Target,
    requiresSubjects: ["household"],
    providesSubjects: ["opportunity"],
    fields: [
      {
        key: "name",
        label: "Name",
        inputType: "string",
        required: true,
        supportsInterpolation: true,
      },
      {
        key: "pipeline_id",
        label: "Pipeline",
        inputType: "select",
        required: false,
        optionsSource: "pipelines",
        helperText: "Leave empty to use the default pipeline for the household type",
      },
      {
        key: "pipeline_stage_id",
        label: "Stage",
        inputType: "select",
        required: false,
        optionsSource: "pipeline_stages",
        parentField: "pipeline_id",
        helperText: "Leave empty to use the first stage of the pipeline",
      },
      {
        key: "value",
        label: "Value",
        inputType: "number",
        required: false,
        helperText: "Opportunity value in dollars (e.g. 1000)",
        supportsInterpolation: true,
      },
      {
        key: "probability",
        label: "Probability",
        inputType: "number",
        required: false,
        helperText: "Win probability 0-100 (e.g. 50)",
        supportsInterpolation: true,
      },
    ],
  },
  {
    key: "action__find_opportunity",
    name: "Find opportunity",
    description: "Find the most recently updated opportunity for the household",
    category: "household",
    nodeType: "action",
    recordType: "household",
    icon: Search,
    requiresSubjects: ["household"],
    providesSubjects: ["opportunity"],
    fields: [
      {
        key: "pipeline_id",
        label: "Pipeline",
        inputType: "select",
        required: false,
        optionsSource: "pipelines",
        helperText: "Leave empty to search all pipelines",
      },
    ],
  },
  {
    key: "action__find_project",
    name: "Find project",
    description: "Find the most recently updated project for the household from a selected template",
    category: "household",
    nodeType: "action",
    recordType: "household",
    icon: Search,
    requiresSubjects: ["household"],
    providesSubjects: ["project"],
    fields: [
      {
        key: "project_template_id",
        label: "Project template",
        inputType: "select",
        required: true,
        optionsSource: "household_project_templates",
      },
    ],
  },
  {
    key: "action__add_to_sequence",
    name: "Add to sequence",
    description: "Add the household to a sequence",
    category: "household",
    nodeType: "action",
    recordType: "household",
    icon: GitMerge,
    requiresSubjects: ["household"],
    fields: [
      {
        key: "sequence_id",
        label: "Sequence",
        inputType: "select",
        required: true,
        optionsSource: "sequences",
      },
      {
        key: "recipients",
        label: "Map household roles in the email",
        inputType: "email_recipients",
        required: true,
        defaultValue: {
          head_of_household: "to",
          spouse_or_partner: "to",
        },
        chatValueShape: '{"head_of_household":"to|cc|bcc|none","spouse_or_partner":"to|cc|bcc|none"}',
      },
    ],
    chatFields: [
      {
        key: "team_member_recipients",
        label: "Additional team members",
        inputType: "json",
        required: false,
        optionsSource: "users",
        chatValueShape: '{"<user id>":"to|cc|bcc|none"}',
      },
    ],
  },
  {
    key: "action__start_project",
    name: "Start project",
    description: "Start a project from a template",
    category: "household",
    nodeType: "action",
    recordType: "household",
    icon: Rocket,
    excludedTriggerSubjects: ["contact"],
    providesSubjects: ["project"],
    fields: [
      {
        key: "project_template_id",
        label: "Project template",
        inputType: "select",
        required: true,
        optionsSource: "project_templates",
      },
      {
        key: "name",
        label: "Project name",
        inputType: "string",
        required: false,
        helperText: "Leave blank to use the project template's default name",
        supportsInterpolation: true,
      },
      {
        key: "assignee",
        label: "Assigned to",
        inputType: "assignment_picker",
        required: false,
        optionsSource: "assignments",
        assignmentTypes: ["user", "custom_field"],
        helperText: "Leave blank to assign the project to the book's principal",
      },
    ],
    chatFields: [
      {
        key: "form_responses",
        label: "First milestone form responses",
        inputType: "json",
        required: false,
        optionsSource: "project_template_form_fields",
        parentField: "project_template_id",
        chatValueShape: '{"<form field id>":"<response>"}',
        supportsInterpolation: true,
        interpolationHelperText:
          "Form response values support Liquid variables from subject data and prior step results.",
      },
    ],
  },
  {
    key: "action__send_intake_form",
    name: "Send intake form",
    description: "Send a published intake form to the household",
    category: "household",
    nodeType: "action",
    recordType: "household",
    icon: FileInput,
    requiresSubjects: ["household"],
    singleBookOnly: true,
    fields: [
      {
        key: "intake_form_id",
        label: "Intake form",
        inputType: "select",
        required: true,
        optionsSource: "intake_forms",
        helperText: "Only published intake forms can be sent",
      },
      {
        key: "delivery_method",
        label: "Send from",
        inputType: "select",
        required: true,
        defaultValue: "slant_email",
        options: [
          { value: "slant_email", label: "Slant notifications" },
          { value: "personal_email", label: "Connected email" },
        ],
      },
      {
        key: "recipients",
        label: "Recipients",
        inputType: "email_recipients",
        required: true,
        defaultValue: {
          head_of_household: "to",
          spouse_or_partner: "none",
        },
        chatValueShape: '{"head_of_household":"to|cc|bcc|none","spouse_or_partner":"to|cc|bcc|none"}',
      },
      {
        key: "creator_copy_method",
        label: "Include the book principal as",
        inputType: "select",
        required: true,
        defaultValue: "bcc",
        visibleWhen: { field: "delivery_method", values: ["slant_email"] },
        options: [
          { value: "bcc", label: "BCC" },
          { value: "cc", label: "CC" },
          { value: "none", label: "None" },
        ],
      },
      {
        key: "reminder_limit",
        label: "Automatic reminders",
        inputType: "select",
        required: true,
        defaultValue: "3",
        visibleWhen: { field: "delivery_method", values: ["slant_email"] },
        options: [
          { value: "0", label: "No automatic reminders" },
          { value: "1", label: "1 reminder" },
          { value: "2", label: "2 reminders" },
          { value: "3", label: "3 reminders" },
          { value: "4", label: "4 reminders" },
          { value: "5", label: "5 reminders" },
          { value: "6", label: "6 reminders" },
          { value: "7", label: "7 reminders" },
          { value: "8", label: "8 reminders" },
          { value: "9", label: "9 reminders" },
          { value: "10", label: "10 reminders" },
        ],
      },
      {
        key: "reminder_interval_days",
        label: "Business days between reminders",
        inputType: "number",
        required: true,
        defaultValue: 3,
        visibleWhen: { field: "delivery_method", values: ["slant_email"] },
        helperText: "Used when automatic reminders are enabled (1–30 days)",
      },
      {
        key: "email_integration_id",
        label: "Connected email",
        inputType: "email_integration",
        required: true,
        optionsSource: "accessible_email_integrations",
        visibleWhen: { field: "delivery_method", values: ["personal_email"] },
      },
      {
        key: "email_content",
        label: "Email content",
        inputType: "email_content",
        required: true,
        defaultValue: {
          email_template_id: null,
          email_template_name: null,
          subject: "",
          body: { html: "" },
        },
        visibleWhen: { field: "delivery_method", values: ["personal_email"] },
        supportsInterpolation: true,
        interpolationHelperText: "Subject and body support variables from subject data and prior step results.",
      },
    ],
  },
  {
    key: "action__remove_from_sequence",
    name: "Remove from sequence",
    description: "Remove the household from a sequence",
    category: "household",
    nodeType: "action",
    recordType: "household",
    icon: GitMerge,
    requiresSubjects: ["household"],
    fields: [
      {
        key: "sequence_id",
        label: "Sequence",
        inputType: "select",
        required: true,
        optionsSource: "sequences",
        options: [{ value: "all", label: "All sequences" }],
        helperText: "Select a specific sequence or remove from all",
      },
    ],
  },
  {
    key: "action__household_delete_open_tasks",
    name: "Delete open tasks",
    description:
      "Delete the household's to-do tasks and stop recurring tasks, including scheduled occurrences. Completed tasks are kept.",
    category: "household",
    nodeType: "action",
    recordType: "household",
    icon: Trash2,
    requiresSubjects: ["household"],
    fields: [],
  },
  {
    key: "action__household_send_email",
    name: "Send email",
    description: "Send an email to the household",
    category: "household",
    nodeType: "action",
    recordType: "household",
    icon: Mail,
    requiresSubjects: ["household"],
    singlePerBranch: true,
    fields: [
      {
        key: "email_integration_id",
        label: "Send from",
        inputType: "email_integration",
        required: true,
        optionsSource: "accessible_email_integrations",
      },
      {
        key: "recipients",
        label: "Recipients",
        inputType: "email_recipients",
        required: true,
        defaultValue: {
          head_of_household: "to",
          spouse_or_partner: "none",
        },
        chatValueShape:
          '{"head_of_household":"to|cc|bcc|none","spouse_or_partner":"to|cc|bcc|none","person?":"to|cc|bcc|none"}',
      },
      {
        key: "email_content",
        label: "Email content",
        inputType: "email_content",
        required: true,
        defaultValue: {
          email_template_id: null,
          email_template_name: null,
          subject: "",
          body: { html: "" },
        },
        optionsSource: "email_templates",
        chatValueShape:
          'Either {"email_template_id":"<id>","email_template_name":"<name>","subject":"","body":{"html":""}} or {"email_template_id":null,"subject":"...","body":{"html":"..."}}',
        supportsInterpolation: true,
        interpolationHelperText: "Subject and body support variables from subject data and prior step results.",
      },
    ],
    chatFields: [
      {
        key: "cc_user_or_team_ids",
        label: "Additional internal recipients",
        inputType: "json",
        required: false,
        optionsSource: "assignments",
        chatValueShape:
          '{"user_ids":["<id after user: prefix>"],"team_ids":["<id after team: prefix>"],"type":"cc|bcc|none"}',
      },
    ],
  },
  {
    key: "action__household_send_sms",
    name: "Send SMS to client",
    description: "Send a text message to the client(s)",
    category: "household",
    nodeType: "action",
    recordType: "household",
    icon: Smartphone,
    requiresSubjects: ["household"],
    singlePerBranch: true,
    fields: [
      {
        key: "sms_integration",
        label: "Send from",
        inputType: "sms_integration",
        required: true,
        optionsSource: "phone_integrations",
      },
      {
        key: "recipients",
        label: "Recipients",
        inputType: "sms_recipients",
        required: true,
        defaultValue: {
          head_of_household: true,
          spouse_or_partner: false,
        },
        chatValueShape: '{"head_of_household":true|false,"spouse_or_partner":true|false,"person?":true|false}',
      },
      {
        key: "message",
        label: "Message",
        inputType: "sms_message",
        required: true,
        placeholder: "Hi {{person.preferred_name}}, ...",
        helperText: "Supports variables like {{person.preferred_name}}",
        rows: 8,
        supportsInterpolation: true,
      },
    ],
  },
  {
    key: "action__household_add_comment",
    name: "Add comment",
    description: "Add a comment to the household",
    category: "household",
    nodeType: "action",
    recordType: "household",
    icon: MessageSquare,
    requiresSubjects: ["household"],
    fields: [
      {
        key: "content",
        label: "Comment",
        inputType: "textarea",
        required: true,
        supportsInterpolation: true,
      },
    ],
  },
  {
    key: "action__promote_to_client",
    name: "Promote to client",
    description: "Promote the household from prospect to client",
    category: "household",
    nodeType: "action",
    recordType: "household",
    icon: TrendingUp,
    requiresSubjects: ["household"],
    fields: [],
  },
  {
    key: "action__update_attribute",
    name: "Update attribute",
    description: "Set a custom field, tier, or next review date on the household",
    category: "household",
    nodeType: "action",
    recordType: "household",
    icon: SquarePen,
    requiresSubjects: ["household"],
    fields: [
      {
        key: "attribute",
        label: "Attribute",
        inputType: "select",
        required: true,
        optionsSource: "household_update_attributes",
      },
      {
        key: "value",
        label: "Value",
        inputType: "attribute_value",
        required: false,
      },
    ],
  },
]
